import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtPct } from "../lib/format";
import ExportMenu from "../components/ExportMenu";

function SectionRow({ label, amount, indent = 0, bold = false, muted = false, positive = false, negative = false }) {
    const color = negative ? "text-neg" : positive ? "text-pos" : bold ? "text-[var(--text-primary)]" : muted ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]";
    return (
        <div className={`flex justify-between py-1.5 text-xs font-mono-fin ${bold ? "font-semibold border-t border-panel mt-1 pt-2" : ""}`}>
            <span style={{ paddingLeft: `${indent * 12}px` }} className={muted ? "text-[var(--text-secondary)]" : "text-[var(--text-primary)]"}>{label}</span>
            <span className={color}>{fmtINR(amount)}</span>
        </div>
    );
}

export default function Statements() {
    const [tab, setTab] = useState("pl");
    const [pl, setPl] = useState(null);
    const [bs, setBs] = useState(null);
    const [cf, setCf] = useState(null);

    useEffect(() => {
        api.get("/accounting/statements/pl").then((r) => setPl(r.data));
        api.get("/accounting/statements/bs").then((r) => setBs(r.data));
        api.get("/accounting/statements/cashflow").then((r) => setCf(r.data));
    }, []);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// FINANCIAL STATEMENTS</div>
                <h1 className="font-display font-black text-3xl">P&L · Balance Sheet · Cash Flow</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    FY 2025-26 (mid-year) · <Link to="/journals" className="text-[var(--brand-amber)] hover:underline">Drill to Journals</Link> · <Link to="/audit" className="text-[var(--brand-amber)] hover:underline">Audit Checks →</Link>
                </div>
            </div>

            <div className="flex items-center gap-2 border-b border-panel">
                {[
                    { key: "pl", label: "Profit & Loss" },
                    { key: "bs", label: "Balance Sheet" },
                    { key: "cf", label: "Cash Flow" },
                ].map((t) => (
                    <button
                        key={t.key}
                        data-testid={`st-tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-xs font-mono-fin uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
                            tab === t.key ? "border-[var(--brand-amber)] text-[var(--text-primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "pl" && pl && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <KpiCard testId="pl-revenue" label="Revenue" value={pl.revenue.total} accent />
                        <KpiCard testId="pl-gp" label="Gross Profit" value={pl.gross_profit} sub={`${pl.gross_margin_pct}%`} />
                        <KpiCard testId="pl-ebitda" label="EBITDA" value={pl.ebitda} sub={`${pl.ebitda_margin_pct}%`} />
                        <KpiCard testId="pl-pbt" label="PBT" value={pl.pbt} sub="Pre-tax" />
                        <KpiCard testId="pl-pat" label="PAT" value={pl.pat} sub={`${pl.net_margin_pct}% NPM`} />
                    </div>

                    <Panel testId="pl-statement" title="Statement of Profit & Loss" subtitle={`${pl.period} · ₹ in Absolute`} right={
                        <ExportMenu kind="pl" testId="export-pl" />
                    }>
                        <div className="max-w-3xl">
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)] mb-2">Revenue</div>
                            {pl.revenue.lines.map((r) => <SectionRow key={r.code} label={r.name} amount={r.amount} indent={1} />)}
                            <SectionRow label="Total Revenue (A)" amount={pl.revenue.total} bold />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mt-4 mb-2">Cost of Sales</div>
                            {pl.cogs.lines.map((r) => <SectionRow key={r.code} label={r.name} amount={-r.amount} indent={1} muted />)}
                            <SectionRow label="Total Cost of Sales (B)" amount={-pl.cogs.total} bold />

                            <SectionRow label="GROSS PROFIT (A – B)" amount={pl.gross_profit} bold positive />
                            <div className="text-[10px] text-[var(--text-muted)] font-mono-fin uppercase tracking-wider mb-3">Gross Margin: {pl.gross_margin_pct}%</div>

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mt-3 mb-2">Indirect Expenses</div>
                            {pl.indirects.lines.map((r) => <SectionRow key={r.code} label={r.name} amount={-r.amount} indent={1} muted />)}
                            <SectionRow label="Total Indirect Expenses (C)" amount={-pl.indirects.total} bold />

                            <SectionRow label="EBITDA (A – B – C)" amount={pl.ebitda} bold positive />
                            <div className="text-[10px] text-[var(--text-muted)] font-mono-fin uppercase tracking-wider mb-3">EBITDA Margin: {pl.ebitda_margin_pct}%</div>

                            <SectionRow label="Less: Depreciation" amount={-pl.depreciation} indent={1} muted />
                            <SectionRow label="EBIT" amount={pl.ebit} bold />
                            <SectionRow label="Less: Finance Costs" amount={-pl.interest} indent={1} muted />
                            <SectionRow label="Profit Before Tax (PBT)" amount={pl.pbt} bold />
                            <SectionRow label="Less: Tax @ 25%" amount={-pl.tax} indent={1} muted />
                            <SectionRow label="PROFIT AFTER TAX (PAT)" amount={pl.pat} bold positive={pl.pat >= 0} negative={pl.pat < 0} />
                            <div className="text-[10px] text-[var(--text-muted)] font-mono-fin uppercase tracking-wider mt-1">Net Profit Margin: {pl.net_margin_pct}% · Mid-year TB (not fully seasonal)</div>
                        </div>
                    </Panel>
                </>
            )}

            {tab === "bs" && bs && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="bs-total-assets" label="Total Assets" value={bs.assets.total} accent />
                        <KpiCard testId="bs-equity" label="Equity" value={bs.equity_and_liabilities.equity.total} />
                        <KpiCard testId="bs-lt" label="Long-Term Debt" value={bs.equity_and_liabilities.long_term.total} />
                        <KpiCard testId="bs-diff" label="Assets − Liab" value={Math.abs(bs.difference)} sub={Math.abs(bs.difference) < 100000 ? "✓ Balanced" : "⚠️ Difference"} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <Panel testId="bs-assets" title="Assets" subtitle={`As of ${bs.as_of}`} right={<ExportMenu kind="bs" testId="export-bs" />}>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mb-2">Fixed Assets</div>
                            {bs.assets.fixed.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Total Fixed Assets (Net)" amount={bs.assets.fixed.total} bold />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mt-3 mb-2">Current Assets</div>
                            {bs.assets.current.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Total Current Assets" amount={bs.assets.current.total} bold />

                            {bs.assets.other.lines.length > 0 && (<>
                                <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mt-3 mb-2">Other Assets</div>
                                {bs.assets.other.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                                <SectionRow label="Total Other Assets" amount={bs.assets.other.total} bold />
                            </>)}

                            <SectionRow label="TOTAL ASSETS" amount={bs.assets.total} bold positive />
                        </Panel>

                        <Panel testId="bs-liab" title="Equity & Liabilities" subtitle={`As of ${bs.as_of}`}>
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neu mb-2">Equity</div>
                            {bs.equity_and_liabilities.equity.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Total Equity" amount={bs.equity_and_liabilities.equity.total} bold />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mt-3 mb-2">Long-Term Liabilities</div>
                            {bs.equity_and_liabilities.long_term.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Total Long-Term" amount={bs.equity_and_liabilities.long_term.total} bold />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mt-3 mb-2">Current Liabilities</div>
                            {bs.equity_and_liabilities.current.lines.map((l) => <SectionRow key={l.code} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Total Current Liabilities" amount={bs.equity_and_liabilities.current.total} bold />

                            <SectionRow label="TOTAL EQUITY & LIABILITIES" amount={bs.equity_and_liabilities.total} bold positive />
                        </Panel>
                    </div>

                    {Math.abs(bs.difference) < 100000 ? (
                        <div className="text-center text-xs font-mono-fin uppercase tracking-wider text-pos">✓ Balance Sheet balances · Assets = Equity + Liabilities</div>
                    ) : (
                        <div className="text-center text-xs font-mono-fin uppercase tracking-wider text-amber">⚠ Reconciling difference of {fmtINR(Math.abs(bs.difference))} — includes prior-year retained earnings adjustment</div>
                    )}
                </>
            )}

            {tab === "cf" && cf && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="cf-ops" label="Cash from Operations" value={cf.operating.total} sub="Operating activities" accent />
                        <KpiCard testId="cf-inv" label="Cash from Investing" value={cf.investing.total} sub="CAPEX / disposals" />
                        <KpiCard testId="cf-fin" label="Cash from Financing" value={cf.financing.total} sub="Debt / equity" />
                        <KpiCard testId="cf-net" label="Net Change in Cash" value={cf.net_change} sub={`Opening ${fmtINR(cf.opening_cash)} → Closing ${fmtINR(cf.closing_cash)}`} />
                    </div>

                    <Panel testId="cf-statement" title="Statement of Cash Flows" subtitle={`${cf.period} · Indirect Method`} right={<ExportMenu kind="cf" testId="export-cf" />}>
                        <div className="max-w-3xl">
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-pos mb-2">A · Cash Flow from Operating Activities</div>
                            {cf.operating.lines.map((l, i) => <SectionRow key={i} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Net Cash from Operating Activities (A)" amount={cf.operating.total} bold positive={cf.operating.total >= 0} negative={cf.operating.total < 0} />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neg mt-4 mb-2">B · Cash Flow from Investing Activities</div>
                            {cf.investing.lines.map((l, i) => <SectionRow key={i} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Net Cash from Investing Activities (B)" amount={cf.investing.total} bold negative />

                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-neu mt-4 mb-2">C · Cash Flow from Financing Activities</div>
                            {cf.financing.lines.map((l, i) => <SectionRow key={i} label={l.name} amount={l.amount} indent={1} muted />)}
                            <SectionRow label="Net Cash from Financing Activities (C)" amount={cf.financing.total} bold />

                            <SectionRow label="Net Change in Cash (A + B + C)" amount={cf.net_change} bold />
                            <SectionRow label="Opening Cash & Cash Equivalents" amount={cf.opening_cash} indent={1} muted />
                            <SectionRow label="CLOSING CASH & CASH EQUIVALENTS" amount={cf.closing_cash} bold positive />
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
}
